import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import toast, { Toaster } from 'react-hot-toast';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { API_BASE, SHOP_URL, parseJsonResponse, getUserErrorMessage } from '../../utils/apiBase';
import { authFetch } from '../../utils/apiAuth';
import SuperPanelShell from '../../components/super/SuperPanelShell';
import ShopInventoryPanel from '../../components/super/ShopInventoryPanel';
import ShopStockLedgerPanel from '../../components/super/ShopStockLedgerPanel';
import ShopMonthPicker from '../../components/super/ShopMonthPicker';
import ShopReceiptModal from '../../components/super/ShopReceiptModal';
import './superPanel.css';

const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'inventory', label: 'Inventory' },
  { id: 'ledger', label: 'Stock Ledger' },
  { id: 'receipts', label: 'Receipts' },
  { id: 'customers', label: 'Customers' },
  { id: 'cashiers', label: 'Cashiers' },
];

function formatMoney(value) {
  return `Rs ${Number(value || 0).toLocaleString('en-PK', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

function formatDate(value) {
  if (!value) return '—';
  return new Date(value).toLocaleString();
}

function formatMonthLabel(monthKey) {
  if (!monthKey) return '—';
  const [year, month] = monthKey.split('-');
  const date = new Date(Number(year), Number(month) - 1, 1);
  return date.toLocaleDateString('en-PK', { month: 'short', year: 'numeric' });
}

function formatShortMonth(monthKey) {
  if (!monthKey) return '';
  const [year, month] = monthKey.split('-');
  const date = new Date(Number(year), Number(month) - 1, 1);
  return date.toLocaleDateString('en-PK', { month: 'short' });
}

function filterReceiptsByPeriod(receipts, period) {
  if (!receipts?.length || period === 'all') return receipts || [];

  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);

  return receipts.filter((receipt) => {
    const createdAt = new Date(receipt.createdAt);
    if (period === 'today') return createdAt >= todayStart;
    if (period === 'month') return createdAt >= monthStart;
    if (/^\d{4}-\d{2}$/.test(period)) {
      const [year, month] = period.split('-').map(Number);
      const start = new Date(year, month - 1, 1);
      const end = new Date(year, month, 1);
      return createdAt >= start && createdAt < end;
    }
    return true;
  });
}

function getReceiptFilterLabel(period) {
  if (period === 'today') return 'Today';
  if (period === 'month') return 'This Month';
  if (period === 'all') return 'All Time';
  if (/^\d{4}-\d{2}$/.test(period)) return formatMonthLabel(period);
  return 'All Time';
}

function isSpecificMonthFilter(period) {
  return /^\d{4}-\d{2}$/.test(period);
}

function buildMonthsFromReceipts(receipts) {
  const map = new Map();

  (receipts || []).forEach((receipt) => {
    if (receipt.voided) return;
    const createdAt = new Date(receipt.createdAt);
    const monthKey = `${createdAt.getFullYear()}-${String(createdAt.getMonth() + 1).padStart(2, '0')}`;
    const row = map.get(monthKey) || { month: monthKey, sales: 0, receipts: 0 };
    row.sales += receipt.total || 0;
    row.receipts += 1;
    map.set(monthKey, row);
  });

  return Array.from(map.values()).sort((a, b) => b.month.localeCompare(a.month));
}

function buildCashierStatsFromReceipts(receipts, period) {
  const filtered = filterReceiptsByPeriod(receipts, period);
  const cashierMap = new Map();

  filtered.forEach((receipt) => {
    if (receipt.voided) return;
    const cashier = receipt.cashier?.trim() || 'Unknown';
    const row = cashierMap.get(cashier) || { cashier, receipts: 0, total: 0 };
    row.receipts += 1;
    row.total += receipt.total || 0;
    cashierMap.set(cashier, row);
  });

  return Array.from(cashierMap.values()).sort((a, b) => b.total - a.total);
}

function buildPaymentStatsFromReceipts(receipts, period) {
  const filtered = filterReceiptsByPeriod(receipts, period).filter((receipt) => !receipt.voided);
  const stats = {
    cash: { receipts: 0, total: 0 },
    online: { receipts: 0, total: 0 },
  };

  filtered.forEach((receipt) => {
    const method = receipt.paymentMethod === 'online' ? 'online' : 'cash';
    stats[method].receipts += 1;
    stats[method].total += receipt.total || 0;
  });

  return stats;
}

function formatPaymentLabel(method) {
  return method === 'online' ? 'Online' : 'Cash';
}

function computeInventoryStats(inventory) {
  let inStock = 0;
  let outOfStock = 0;
  let lowStock = 0;
  let totalUnits = 0;
  let stockValue = 0;

  (inventory || []).forEach((product) => {
    const stock = Number(product.stock || 0);
    totalUnits += stock;
    stockValue += stock * (product.price || 0);
    if (stock <= 0) outOfStock += 1;
    else {
      inStock += 1;
      if (stock <= 5) lowStock += 1;
    }
  });

  return {
    totalProducts: inventory?.length || 0,
    inStock,
    outOfStock,
    lowStock,
    totalUnits,
    stockValue,
  };
}

function ReceiptSummaryCard({ label, value, hint, gold }) {
  return (
    <div className="sp-receipt-stat-card">
      <span className="sp-stat-label">{label}</span>
      <strong className={`sp-stat-value ${gold ? 'sp-stat-gold' : ''}`}>{value}</strong>
      {hint ? <span className="sp-stat-hint">{hint}</span> : null}
    </div>
  );
}

function StatCard({ label, value, hint, gold, onClick, actionLabel }) {
  return (
    <button type="button" className="sp-stat-card sp-stat-card--clickable" onClick={onClick}>
      <span className="sp-stat-label">{label}</span>
      <strong className={`sp-stat-value ${gold ? 'sp-stat-gold' : ''}`}>{value}</strong>
      <span className="sp-stat-hint">{hint}</span>
      <span className="sp-stat-cta">{actionLabel}</span>
    </button>
  );
}

const CHART_TOOLTIP_STYLE = {
  background: '#12121f',
  border: '1px solid rgba(212, 175, 55, 0.35)',
  borderRadius: 10,
  color: '#e8e0cc',
  fontSize: 13,
  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.45)',
};

function TopSellingProducts({ products }) {
  const totalRevenue = useMemo(
    () => products.reduce((sum, item) => sum + (item.revenue || 0), 0),
    [products]
  );
  const maxRevenue = products[0]?.revenue || 1;

  return (
    <div className="sp-top-products">
      {products.map((item, index) => {
        const rank = index + 1;
        const share = totalRevenue ? Math.round((item.revenue / totalRevenue) * 100) : 0;
        const barWidth = Math.max(8, Math.round((item.revenue / maxRevenue) * 100));

        return (
          <div key={item.name} className="sp-top-product-row">
            <div className={`sp-top-product-rank sp-top-product-rank--${Math.min(rank, 4)}`}>
              {rank}
            </div>
            <div className="sp-top-product-body">
              <div className="sp-top-product-head">
                <span className="sp-top-product-name" title={item.name}>
                  {item.name}
                </span>
                <strong className="sp-top-product-revenue">{formatMoney(item.revenue)}</strong>
              </div>
              <div className="sp-top-product-bar-track">
                <div className="sp-top-product-bar-fill" style={{ width: `${barWidth}%` }} />
              </div>
              <div className="sp-top-product-meta">
                <span>{item.quantitySold} units sold</span>
                <span>{share}% of top sales</span>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function SalesByMonthChart({ rows }) {
  const chartData = useMemo(() => {
    return [...rows]
      .sort((a, b) => a.month.localeCompare(b.month))
      .slice(-8)
      .map((row) => ({
        ...row,
        label: formatShortMonth(row.month),
        fullLabel: formatMonthLabel(row.month),
      }));
  }, [rows]);

  const peakMonth = useMemo(() => {
    if (!rows.length) return null;
    return [...rows].sort((a, b) => b.sales - a.sales)[0];
  }, [rows]);

  const latestMonth = rows[0];

  return (
    <div className="sp-month-chart-block">
      <div className="sp-month-chart-kpis">
        <div className="sp-month-kpi">
          <span className="sp-month-kpi-label">Latest month</span>
          <strong className="sp-month-kpi-value">{formatMoney(latestMonth?.sales)}</strong>
          <span className="sp-month-kpi-hint">
            {latestMonth ? formatMonthLabel(latestMonth.month) : '—'} · {latestMonth?.receipts || 0} receipts
          </span>
        </div>
        <div className="sp-month-kpi">
          <span className="sp-month-kpi-label">Best month</span>
          <strong className="sp-month-kpi-value sp-stat-gold">{formatMoney(peakMonth?.sales)}</strong>
          <span className="sp-month-kpi-hint">
            {peakMonth ? formatMonthLabel(peakMonth.month) : '—'} · {peakMonth?.receipts || 0} receipts
          </span>
        </div>
      </div>

      <div className="sp-month-chart-wrap">
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={chartData} margin={{ top: 12, right: 12, left: 4, bottom: 8 }} barCategoryGap="18%">
            <defs>
              <linearGradient id="shopSalesBar" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#f5d76e" stopOpacity={0.95} />
                <stop offset="100%" stopColor="#9a7b1a" stopOpacity={0.85} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" vertical={false} />
            <XAxis
              dataKey="label"
              tickLine={false}
              axisLine={false}
              tick={{ fill: '#8b8698', fontSize: 12 }}
            />
            <YAxis
              tickLine={false}
              axisLine={false}
              tick={{ fill: '#8b8698', fontSize: 11 }}
              tickFormatter={(value) => (value >= 1000 ? `${Math.round(value / 1000)}k` : value)}
              width={42}
            />
            <Tooltip
              cursor={{ fill: 'rgba(212, 175, 55, 0.08)' }}
              contentStyle={CHART_TOOLTIP_STYLE}
              labelStyle={{ color: '#f5d76e', marginBottom: 6, fontWeight: 700 }}
              formatter={(value, name) => {
                if (name === 'sales') return [formatMoney(value), 'Sales'];
                return [value, 'Receipts'];
              }}
              labelFormatter={(_, payload) => payload?.[0]?.payload?.fullLabel || ''}
            />
            <Bar dataKey="sales" fill="url(#shopSalesBar)" radius={[8, 8, 0, 0]} maxBarSize={48} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="sp-month-mini-list">
        {rows.slice(0, 6).map((row) => (
          <div key={row.month} className="sp-month-mini-row">
            <span className="sp-month-mini-label">{formatMonthLabel(row.month)}</span>
            <span className="sp-month-mini-receipts">{row.receipts} receipts</span>
            <strong className="sp-month-mini-sales">{formatMoney(row.sales)}</strong>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function ShopDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [receiptFilter, setReceiptFilter] = useState('all');
  const [cashierFilter, setCashierFilter] = useState('all');
  const [selectedReceiptId, setSelectedReceiptId] = useState(null);
  const [data, setData] = useState(null);
  const [loggingInShop, setLoggingInShop] = useState(false);

  const openDetail = useCallback((tab, filter = 'all') => {
    setActiveTab(tab);
    if (tab === 'receipts') {
      setReceiptFilter(filter);
    }
  }, []);

  const handleTabChange = useCallback((tabId) => {
    setActiveTab(tabId);
    if (tabId === 'receipts') {
      setReceiptFilter('all');
    }
  }, []);

  const handleInventoryChange = useCallback((nextInventory) => {
    setData((prev) => {
      if (!prev) return prev;
      const inventoryStats = computeInventoryStats(nextInventory);
      return {
        ...prev,
        inventory: nextInventory,
        analytics: {
          ...prev.analytics,
          inventoryStats,
          inventoryValue: inventoryStats.stockValue,
        },
      };
    });
  }, []);

  const fetchDetail = useCallback(async () => {
    setLoading(true);
    try {
      const response = await authFetch(`${API_BASE}/v1/shop/accounts/${id}/detail`);
      const result = await parseJsonResponse(response);
      setData(result.data);
    } catch (error) {
      setData(null);
      toast.error(getUserErrorMessage(error, 'Unable to load shop details.'));
      navigate('/super/shops');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  if (loading) {
    return (
      <SuperPanelShell title="Loading..." breadcrumb="Super Panel • Shop" showBack>
        <p className="sp-muted">Loading shop details...</p>
      </SuperPanelShell>
    );
  }

  if (!data) return null;

  const { account, analytics, inventory, recentReceipts } = data;
  const filteredReceipts = filterReceiptsByPeriod(recentReceipts, receiptFilter);
  const receiptFilterLabel = getReceiptFilterLabel(receiptFilter);
  const cashierFilterLabel = getReceiptFilterLabel(cashierFilter);
  const filteredCashierStats = buildCashierStatsFromReceipts(recentReceipts, cashierFilter);
  const filteredPaymentStats = buildPaymentStatsFromReceipts(recentReceipts, receiptFilter);
  const availableMonths = analytics.salesByMonth?.length
    ? [...analytics.salesByMonth].sort((a, b) => b.month.localeCompare(a.month))
    : buildMonthsFromReceipts(recentReceipts);

  const activeFilteredReceipts = filteredReceipts.filter((r) => !r.voided);
  const filteredSalesTotal = activeFilteredReceipts.reduce((sum, receipt) => sum + (receipt.total || 0), 0);
  const filteredDiscountTotal = activeFilteredReceipts.reduce(
    (sum, receipt) => sum + (receipt.discount || 0),
    0,
  );
  const cashierSalesTotal = filteredCashierStats.reduce((sum, row) => sum + (row.total || 0), 0);
  const cashierReceiptsTotal = filteredCashierStats.reduce((sum, row) => sum + (row.receipts || 0), 0);

  const handleLoginShop = async () => {
    if (!account.isActive) {
      toast.error('This shop is inactive.');
      return;
    }

    setLoggingInShop(true);
    try {
      const response = await authFetch(`${API_BASE}/v1/shop/accounts/${id}/impersonate`, {
        method: 'POST',
      });
      const result = await parseJsonResponse(response);
      const params = new URLSearchParams({
        token: result.token,
        impersonate: '1',
      });
      const opened = window.open(`${SHOP_URL}?${params.toString()}`, '_blank', 'noopener,noreferrer');
      if (!opened) {
        toast.error('Popup blocked. Allow popups to open the shop panel.');
        return;
      }
      toast.success(`Opening ${account.shopName}...`);
    } catch (error) {
      toast.error(getUserErrorMessage(error, 'Unable to login to this shop.'));
    } finally {
      setLoggingInShop(false);
    }
  };

  return (
    <SuperPanelShell
      title={account.shopName}
      titleExtra={
        <span className={`sp-role-badge ${account.isActive ? 'sp-role-admin' : 'sp-role-suspended'}`}>
          {account.isActive ? 'Active' : 'Inactive'}
        </span>
      }
      breadcrumb={`Super Panel • Shops • ${account.city}`}
      showBack
      action={
        <>
          <button
            type="button"
            className="sp-btn sp-btn--gold"
            disabled={!account.isActive || loggingInShop}
            onClick={handleLoginShop}
          >
            {loggingInShop ? 'Opening...' : 'Login Shop'}
          </button>
          <button type="button" className="sp-btn sp-btn--ghost" onClick={() => navigate(`/super/shops/edit/${id}`)}>
            Edit Shop
          </button>
        </>
      }
    >
      <Toaster
        position="top-center"
        toastOptions={{
          style: { background: '#12121f', color: '#f5d76e', border: '1px solid rgba(212,175,55,0.3)' },
        }}
      />

      <div className="sp-shop-detail">
      <div className="sp-card">
        <div className="sp-card-body sp-card-body--pad">
          <div className="sp-shop-info-grid">
            <div className="sp-shop-info-item">
              <span className="sp-shop-info-label">City</span>
              <strong className="sp-shop-info-value">{account.city}</strong>
            </div>
            <div className="sp-shop-info-item">
              <span className="sp-shop-info-label">Contact</span>
              <strong className="sp-shop-info-value">{account.name}</strong>
            </div>
            <div className="sp-shop-info-item">
              <span className="sp-shop-info-label">Email</span>
              <strong className="sp-shop-info-value">{account.email}</strong>
            </div>
            <div className="sp-shop-info-item">
              <span className="sp-shop-info-label">Phone</span>
              <strong className="sp-shop-info-value">{account.phone}</strong>
            </div>
            <div className="sp-shop-info-item">
              <span className="sp-shop-info-label">Password</span>
              <strong className="sp-shop-info-value">
                <code>{account.password}</code>
              </strong>
            </div>
            <div className="sp-shop-info-item">
              <span className="sp-shop-info-label">Created</span>
              <strong className="sp-shop-info-value">{formatDate(account.createdAt)}</strong>
            </div>
          </div>
        </div>
      </div>

      <div className="sp-tab-row">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={`sp-tab-btn ${activeTab === tab.id ? 'sp-tab-btn--active' : ''}`}
            onClick={() => handleTabChange(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'overview' && (
        <>
          <div className="sp-stats-row sp-stats-row--3">
            <StatCard
              label="Total Sales"
              value={formatMoney(analytics.totalSales)}
              hint={`${analytics.totalReceipts} receipts`}
              gold
              actionLabel="View all receipts →"
              onClick={() => openDetail('receipts', 'all')}
            />
            <StatCard
              label="This Month"
              value={formatMoney(analytics.monthSales)}
              hint={`${analytics.monthReceipts} receipts`}
              actionLabel="View this month →"
              onClick={() => openDetail('receipts', 'month')}
            />
            <StatCard
              label="Today"
              value={formatMoney(analytics.todaySales)}
              hint={`${analytics.todayReceipts} receipts`}
              actionLabel="View today →"
              onClick={() => openDetail('receipts', 'today')}
            />
          </div>

          <div className="sp-stats-row sp-stats-row--3">
            <StatCard
              label="Products"
              value={analytics.totalProducts}
              hint="In inventory"
              actionLabel="View inventory →"
              onClick={() => openDetail('inventory')}
            />
            <StatCard
              label="Customers"
              value={analytics.totalCustomers}
              hint="With receipts"
              actionLabel="View customers →"
              onClick={() => openDetail('customers')}
            />
            <StatCard
              label="Inventory Value"
              value={formatMoney(analytics.inventoryValue)}
              hint="Stock qty × price"
              actionLabel="Manage inventory →"
              onClick={() => openDetail('inventory')}
            />
          </div>

          <div className="sp-stats-row sp-stats-row--3">
            <StatCard
              label="Cash Collected"
              value={formatMoney(analytics.cashSales)}
              hint={`${analytics.cashReceipts || 0} receipts · all time`}
              gold
              actionLabel="View receipts →"
              onClick={() => openDetail('receipts', 'all')}
            />
            <StatCard
              label="Online Collected"
              value={formatMoney(analytics.onlineSales)}
              hint={`${analytics.onlineReceipts || 0} receipts · all time`}
              actionLabel="View receipts →"
              onClick={() => openDetail('receipts', 'all')}
            />
            <StatCard
              label="Payment Split"
              value={
                analytics.totalSales
                  ? `${Math.round(((analytics.cashSales || 0) / analytics.totalSales) * 100)}% cash`
                  : '—'
              }
              hint={
                analytics.totalSales
                  ? `${Math.round(((analytics.onlineSales || 0) / analytics.totalSales) * 100)}% online`
                  : 'No sales yet'
              }
              actionLabel="Shop reports in panel →"
              onClick={() => openDetail('receipts', 'all')}
            />
          </div>

          <div className="sp-analytics-grid">
            <div className="sp-card sp-analytics-card">
              <div className="sp-card-body sp-card-body--pad">
                <div className="sp-analytics-card-head">
                  <div>
                    <h3 className="sp-section-title sp-section-title--flush">Top Selling Products</h3>
                    <p className="sp-section-desc">Best performers by revenue across all receipts</p>
                  </div>
                  {analytics.topProducts?.length > 0 && (
                    <span className="sp-analytics-pill">{analytics.topProducts.length} products</span>
                  )}
                </div>
                {analytics.topProducts?.length ? (
                  <TopSellingProducts products={analytics.topProducts} />
                ) : (
                  <p className="sp-muted sp-muted--compact">No sales data yet.</p>
                )}
              </div>
            </div>

            <div className="sp-card sp-analytics-card">
              <div className="sp-card-body sp-card-body--pad">
                <div className="sp-analytics-card-head">
                  <div>
                    <h3 className="sp-section-title sp-section-title--flush">Sales by Month</h3>
                    <p className="sp-section-desc">Monthly revenue trend and receipt volume</p>
                  </div>
                  {analytics.salesByMonth?.length > 0 && (
                    <span className="sp-analytics-pill">{analytics.salesByMonth.length} months</span>
                  )}
                </div>
                {analytics.salesByMonth?.length ? (
                  <SalesByMonthChart rows={analytics.salesByMonth} />
                ) : (
                  <p className="sp-muted sp-muted--compact">No monthly data yet.</p>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {activeTab === 'inventory' && (
        <div className="sp-card">
          <div className="sp-card-body sp-card-body--pad">
            <ShopInventoryPanel
              shopAccountId={id}
              inventory={inventory}
              inventoryStats={analytics.inventoryStats}
              onInventoryChange={handleInventoryChange}
            />
          </div>
        </div>
      )}

      {activeTab === 'ledger' && (
        <div className="sp-card">
          <div className="sp-card-body sp-card-body--pad">
            <ShopStockLedgerPanel shopAccountId={id} />
          </div>
        </div>
      )}

      {activeTab === 'receipts' && (
        <div className="sp-card">
          <div className="sp-card-body sp-card-body--pad">
            <div className="sp-detail-head">
              <div>
                <h3 className="sp-section-title sp-section-title--flush">
                  Receipts — {receiptFilterLabel}
                </h3>
                <p className="sp-section-desc">Filter receipts by day, current month, or any past month</p>
              </div>
            </div>

            <div className="sp-receipt-filters">
              <div className="sp-filter-row sp-filter-row--flush">
                <button
                  type="button"
                  className={`sp-filter-chip ${receiptFilter === 'today' ? 'sp-filter-chip--active' : ''}`}
                  onClick={() => setReceiptFilter('today')}
                >
                  Today
                </button>
                <button
                  type="button"
                  className={`sp-filter-chip ${receiptFilter === 'month' ? 'sp-filter-chip--active' : ''}`}
                  onClick={() => setReceiptFilter('month')}
                >
                  This Month
                </button>
                <button
                  type="button"
                  className={`sp-filter-chip ${receiptFilter === 'all' ? 'sp-filter-chip--active' : ''}`}
                  onClick={() => setReceiptFilter('all')}
                >
                  All Time
                </button>
              </div>

              <ShopMonthPicker
                value={isSpecificMonthFilter(receiptFilter) ? receiptFilter : ''}
                onChange={(monthKey) => setReceiptFilter(monthKey || 'all')}
                monthsData={availableMonths}
                placeholder="Pick a month"
              />
            </div>

            <div className="sp-stats-row sp-stats-row--3 sp-receipt-summary-row">
              <ReceiptSummaryCard
                label="Total Sales"
                value={formatMoney(filteredSalesTotal)}
                hint={receiptFilterLabel}
                gold
              />
              <ReceiptSummaryCard
                label="Receipts"
                value={activeFilteredReceipts.length}
                hint={
                  filteredReceipts.length > activeFilteredReceipts.length
                    ? `${filteredReceipts.length - activeFilteredReceipts.length} voided leftover`
                    : `In ${receiptFilterLabel.toLowerCase()}`
                }
              />
              <ReceiptSummaryCard
                label="Total Discount"
                value={formatMoney(filteredDiscountTotal)}
                hint="Active receipts only"
              />
            </div>

            <div className="sp-stats-row sp-stats-row--3 sp-receipt-summary-row">
              <ReceiptSummaryCard
                label="Cash"
                value={formatMoney(filteredPaymentStats.cash.total)}
                hint={`${filteredPaymentStats.cash.receipts} receipt(s)`}
                gold
              />
              <ReceiptSummaryCard
                label="Online"
                value={formatMoney(filteredPaymentStats.online.total)}
                hint={`${filteredPaymentStats.online.receipts} receipt(s)`}
              />
              <ReceiptSummaryCard
                label="Payment mix"
                value={
                  filteredSalesTotal
                    ? `${Math.round((filteredPaymentStats.cash.total / filteredSalesTotal) * 100)}% cash`
                    : '—'
                }
                hint={
                  filteredSalesTotal
                    ? `${Math.round((filteredPaymentStats.online.total / filteredSalesTotal) * 100)}% online`
                    : 'No sales in period'
                }
              />
            </div>

            {filteredReceipts.length ? (
              <div className="sp-table-wrap">
                <table className="sp-table">
                  <thead>
                    <tr>
                      <th>Invoice</th>
                      <th>Date</th>
                      <th>Cashier</th>
                      <th>Payment</th>
                      <th>Items</th>
                      <th>Discount</th>
                      <th>Total</th>
                      <th>Customer</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {filteredReceipts.map((receipt) => (
                      <tr
                        key={receipt.id}
                        className={`sp-table-row--clickable${receipt.voided ? ' sp-table-row--voided' : ''}`}
                        onClick={() => setSelectedReceiptId(receipt.id)}
                      >
                        <td>
                          #{receipt.invoiceNumber}
                          {receipt.voided ? (
                            <span className="sp-void-badge" title={receipt.voidReason || 'Voided'}>
                              VOID
                            </span>
                          ) : null}
                        </td>
                        <td>{receipt.date} {receipt.time}</td>
                        <td>{receipt.cashier}</td>
                        <td>{formatPaymentLabel(receipt.paymentMethod)}</td>
                        <td>{receipt.itemsCount}</td>
                        <td>{formatMoney(receipt.discount)}</td>
                        <td>{formatMoney(receipt.total)}</td>
                        <td>{receipt.customerName || receipt.customerPhone || '—'}</td>
                        <td>
                          <button
                            type="button"
                            className="sp-btn sp-btn--ghost sp-btn--sm"
                            onClick={(event) => {
                              event.stopPropagation();
                              setSelectedReceiptId(receipt.id);
                            }}
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="sp-muted sp-muted--compact">No receipts for this period.</p>
            )}
          </div>
        </div>
      )}

      {activeTab === 'customers' && (
        <div className="sp-card">
          <div className="sp-card-body sp-card-body--pad">
            <h3 className="sp-section-title">Customers ({analytics.customers?.length || 0})</h3>
            {analytics.customers?.length ? (
              <div className="sp-table-wrap">
                <table className="sp-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Phone</th>
                      <th>Receipts</th>
                      <th>Total Spent</th>
                      <th>Last Visit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {analytics.customers.map((customer) => (
                      <tr key={`${customer.phone}-${customer.name}`}>
                        <td>{customer.name}</td>
                        <td>{customer.phone}</td>
                        <td>{customer.totalReceipts}</td>
                        <td>{formatMoney(customer.totalSpent)}</td>
                        <td>{formatDate(customer.lastVisit)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="sp-muted">No customer data yet.</p>
            )}
          </div>
        </div>
      )}

      {activeTab === 'cashiers' && (
        <div className="sp-card">
          <div className="sp-card-body sp-card-body--pad">
            <div className="sp-detail-head">
              <div>
                <h3 className="sp-section-title sp-section-title--flush">
                  Cashier Performance — {cashierFilterLabel}
                </h3>
                <p className="sp-section-desc">
                  Filter by day, current month, or any past month
                </p>
              </div>
            </div>

            <div className="sp-receipt-filters">
              <div className="sp-filter-row sp-filter-row--flush">
                <button
                  type="button"
                  className={`sp-filter-chip ${cashierFilter === 'today' ? 'sp-filter-chip--active' : ''}`}
                  onClick={() => setCashierFilter('today')}
                >
                  Today
                </button>
                <button
                  type="button"
                  className={`sp-filter-chip ${cashierFilter === 'month' ? 'sp-filter-chip--active' : ''}`}
                  onClick={() => setCashierFilter('month')}
                >
                  This Month
                </button>
                <button
                  type="button"
                  className={`sp-filter-chip ${cashierFilter === 'all' ? 'sp-filter-chip--active' : ''}`}
                  onClick={() => setCashierFilter('all')}
                >
                  All Time
                </button>
              </div>

              <ShopMonthPicker
                value={isSpecificMonthFilter(cashierFilter) ? cashierFilter : ''}
                onChange={(monthKey) => setCashierFilter(monthKey || 'all')}
                monthsData={availableMonths}
                placeholder="Pick a month"
              />
            </div>

            <div className="sp-stats-row sp-stats-row--3 sp-receipt-summary-row">
              <ReceiptSummaryCard
                label="Total Sales"
                value={formatMoney(cashierSalesTotal)}
                hint={cashierFilterLabel}
                gold
              />
              <ReceiptSummaryCard
                label="Receipts"
                value={cashierReceiptsTotal}
                hint={`In ${cashierFilterLabel.toLowerCase()}`}
              />
              <ReceiptSummaryCard
                label="Cashiers"
                value={filteredCashierStats.length}
                hint="With sales in period"
              />
            </div>

            {filteredCashierStats.length ? (
              <div className="sp-table-wrap">
                <table className="sp-table">
                  <thead>
                    <tr>
                      <th>Cashier</th>
                      <th>Receipts</th>
                      <th>Total Sales</th>
                      <th>Share</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredCashierStats.map((row) => {
                      const share = cashierSalesTotal
                        ? Math.round((row.total / cashierSalesTotal) * 100)
                        : 0;
                      return (
                        <tr key={row.cashier}>
                          <td>
                            <strong>{row.cashier}</strong>
                          </td>
                          <td>{row.receipts}</td>
                          <td>{formatMoney(row.total)}</td>
                          <td>
                            <span className="sp-analytics-pill">{share}%</span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="sp-muted">No cashier data for {cashierFilterLabel.toLowerCase()}.</p>
            )}
          </div>
        </div>
      )}
      </div>

      <ShopReceiptModal
        open={Boolean(selectedReceiptId)}
        shopAccountId={id}
        receiptId={selectedReceiptId}
        shopName={account.shopName}
        city={account.city}
        onClose={() => setSelectedReceiptId(null)}
        onDeleted={() => {
          setSelectedReceiptId(null);
          fetchDetail();
        }}
      />
    </SuperPanelShell>
  );
}
